import { useState } from "react";

import { Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import { invariant } from "ts-invariant";

import { useGetHealthCareProviders, useGetInvoices, useGetProfessionals } from "api/services/admin/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { MUISelect } from "shared/atoms/MUI";
import { View } from "shared/containers/View";
import type { Breadcrumb } from "shared/containers/View/components/ViewBreadcrumbs";
import { MarketTypes } from "types";
import { isUserMarket, userIsAdmin, userIsBillingAdmin } from "utils/profile/profileHelper";

import {
  ActivePatients,
  CommissionRates,
  Earnings,
  EnrollmentGoals,
  InvoiceSpecificationList,
  Overview,
  PatientEnrollment,
  PendingTreatmentActivityLogs,
  PerformanceSummary,
  Subscription,
  TeamMembers,
  UnsignedNotes,
} from "../components";
import { InvoiceNotificationBox } from "../components/InvoiceNotificationBox";

const ALL_CLINICS = "all";

export const AdminView: React.FC = () => {
  const { profile } = useProfileContext();
  const { t } = useTranslation();
  const [selectedHcpID, setSelectedHcpID] = useState<number | typeof ALL_CLINICS>(ALL_CLINICS);

  invariant(profile, "Profile must be defined");

  const isUS = isUserMarket(MarketTypes.US, profile);
  const hcoID = profile.health_care_organization?.id ?? 0;
  const isAdmin = userIsAdmin(profile) || userIsBillingAdmin(profile);
  const isPlatformPerSeat = profile.health_care_organization?.platform_fee_model === "per_seat";
  const hasStripeMetadata = !!profile.health_care_organization?.stripe_metadata;

  const { data: invoicesData } = useGetInvoices(
    { health_care_organization_id: hcoID, params: { status: "open" } },
    { enabled: isAdmin && hasStripeMetadata }
  );
  const invoices = invoicesData?.invoices ?? [];

  const { data } = useGetHealthCareProviders({
    health_care_organization_id: hcoID,
  });

  const { data: professionalsData } = useGetProfessionals({
    health_care_organization_id: hcoID,
    params: selectedHcpID !== ALL_CLINICS ? { hcp_id: selectedHcpID } : undefined,
  });
  const professionals = professionalsData?.professionals || [];

  if (!profile.health_care_organization) {
    return <Navigate to="/dashboard" replace />;
  }

  const selectedHcp = data?.health_care_providers.find(provider => provider.id === selectedHcpID);
  const additionalBreadcrumbs: Breadcrumb[] = selectedHcp
    ? [{ label: selectedHcp.name, href: `/admin/${selectedHcp.id}` }]
    : [];

  return (
    <View
      title={t("views.admin")}
      additionalBreadcrumbs={additionalBreadcrumbs}
      actionButtons={[
        <MUISelect
          size="small"
          options={[
            { label: t("admin.all_clinics"), value: ALL_CLINICS },
            ...(data?.health_care_providers.map(provider => ({ label: provider.name, value: provider.id })) ?? []),
          ]}
          value={selectedHcpID}
          onChange={e => setSelectedHcpID(e.target.value as number)}
          sx={{
            minWidth: 200,
            height: 40,
          }}
        />,
      ]}
    >
      <Grid2 container spacing={3} columns={12}>
        <StyledGrid2 size={12}>
          <Overview
            clinicName={selectedHcp?.name}
            hcoID={profile.health_care_organization.id}
            hcpID={selectedHcp?.id}
            professionalsCount={professionals.length}
          />
        </StyledGrid2>

        {isAdmin && isPlatformPerSeat && hasStripeMetadata && (
          <StyledGrid2 size={12}>
            <Subscription hcoID={profile.health_care_organization.id} professionalsCount={professionals.length} />
          </StyledGrid2>
        )}

        <StyledGrid2 size={{ xs: 12, md: 6 }}>
          <ActivePatients
            hcoID={profile.health_care_organization.id}
            hcpID={selectedHcp?.id}
            professionals={professionals}
          />
        </StyledGrid2>
        <StyledGrid2 size={{ xs: 12, md: 6 }}>
          <PatientEnrollment
            hcoID={profile.health_care_organization.id}
            hcpID={selectedHcp?.id}
            professionals={professionals}
          />
        </StyledGrid2>

        {isUS && (
          <StyledGrid2 size={12}>
            <Earnings
              hcoID={profile.health_care_organization.id}
              hcpID={selectedHcp?.id}
              professionals={professionals}
            />
          </StyledGrid2>
        )}

        <StyledGrid2 size={{ xs: 12, md: 6 }}>
          <PerformanceSummary hcoID={profile.health_care_organization.id} />
        </StyledGrid2>
        <StyledGrid2 size={{ xs: 12, md: 6 }}>
          <EnrollmentGoals hcoID={profile.health_care_organization.id} professionals={professionals} />
        </StyledGrid2>

        <StyledGrid2 size={12}>
          <CommissionRates hcoID={profile.health_care_organization.id} />
        </StyledGrid2>

        <StyledGrid2 size={12}>
          <TeamMembers hcpID={selectedHcp?.id} hcoID={profile.health_care_organization.id} />
        </StyledGrid2>

        {isAdmin && isUS && !isPlatformPerSeat && (
          <StyledGrid2 size={12}>
            <InvoiceSpecificationList hcoID={profile.health_care_organization.id} />
          </StyledGrid2>
        )}

        {isAdmin &&
          invoices?.map(invoice => (
            <Grid2 key={invoice.invoice_id} size={12}>
              <InvoiceNotificationBox invoice={invoice} />
            </Grid2>
          ))}

        <StyledGrid2 size={12}>
          <UnsignedNotes hcoID={profile.health_care_organization.id} />
        </StyledGrid2>

        <StyledGrid2 size={12}>
          <PendingTreatmentActivityLogs hcoID={profile.health_care_organization.id} />
        </StyledGrid2>
      </Grid2>
    </View>
  );
};

const StyledGrid2 = styled(Grid2)`
  padding: 32px;
  background-color: #fff;
  border-radius: 12px;
`;
